import React from 'react';

import type { i18n } from 'i18next';
import { z } from 'zod';

import { EventStatus } from 'lane-shared/domains/visitorManagement/types';
import {
  MjmlSection,
  MjmlColumn,
  MjmlImage,
  MjmlText,
  MjmlButton,
} from 'lane-shared/emails/MjmlComponentsV2';

import {
  buildingSchema,
  hostSchema,
  visitorPassSchema,
  visitorSchema,
  credentialSchema,
  appleWalletSchema,
  calendarLinksSchema,
} from '../schema';

export function EmailVisitorPass({
  i18n,
  building,
  visitor,
  visitorPass,
  credential,
  host,
  appleWallet,
  calendarLinks,
  passIsUpdated,
}: {
  i18n: i18n;
  building: z.infer<typeof buildingSchema>;
  visitor: z.infer<typeof visitorSchema>;
  visitorPass: z.infer<typeof visitorPassSchema>;
  host: z.infer<typeof hostSchema>;
  credential?: z.infer<typeof credentialSchema>;
  appleWallet?: z.infer<typeof appleWalletSchema>;
  calendarLinks?: z.infer<typeof calendarLinksSchema>;
  passIsUpdated?: boolean;
}) {
  const passIsCancelled = [
    EventStatus.EVENT_STATUS_INVALID,
    EventStatus.EVENT_STATUS_CANCELLED,
    EventStatus.EVENT_STATUS_WITHDRAWN,
  ].includes(visitorPass.status);

  const isPassStatusValidated =
    visitorPass.status === EventStatus.EVENT_STATUS_VALIDATED;

  const isCredentialShown = building?.qrEnabled || isPassStatusValidated;

  // As long as visitor validation is not enabled, we show "valid" text for upcoming and updated passes
  // Once visitor validation is enabled, we only show "valid" text for validated passes
  const isPassValidTextShown =
    !building.visitorValidationEnabled ||
    (building?.visitorValidationEnabled && isPassStatusValidated);

  const getPassValidityText = () => {
    if (passIsCancelled) {
      return i18n.t('shared.email.visitorManagement.visitorPass.passNotValid');
    }

    if (isPassValidTextShown) {
      return i18n.t('shared.email.visitorManagement.visitorPass.passValid');
    }

    return null;
  };

  const credentialImage = (
    <MjmlImage
      padding="0"
      css-class="qr-code"
      width="150px"
      src={credential?.uri || credential?.data}
      alt={i18n.t(
        'shared.email.visitorManagement.visitorPass.credentialAltText'
      )}
    />
  );

  return (
    <MjmlSection padding-top="0%" padding="5%" background-color="#FFFFFF">
      <MjmlColumn
        padding="24px 8px 24px 8px"
        width="100%"
        css-class="bordered-col"
      >
        {passIsCancelled && (
          <MjmlButton
            color="#0F121F"
            border-radius="24px"
            border="1px solid #FFCDC7"
            background-color="#FFF1F0"
          >
            {i18n.t(
              'shared.email.visitorManagement.visitorPass.status.cancelled'
            )}
          </MjmlButton>
        )}

        {passIsUpdated && (
          <MjmlButton
            color="#0F121F"
            border-radius="24px"
            border="1px solid #AFEEBB"
            background-color="#E9FCEE"
          >
            {i18n.t('Updated')}
          </MjmlButton>
        )}
        <MjmlText
          align="center"
          color="#1D1E20"
          paddingTop="0px"
          paddingBottom="0px"
          fontSize="16px"
        >
          <h5 style={{ margin: '0px' }}>
            {`${building.name} ${i18n.t('shared.email.visitorManagement.visitorPass.visitorPass')} ${visitor.firstName} ${visitor.lastName}`}
          </h5>
        </MjmlText>

        {credential && isCredentialShown && credentialImage}

        {getPassValidityText() && (
          <MjmlText
            align="center"
            paddingTop="24px"
            paddingBottom="0px"
            color="#1D1E20"
            fontSize="16px"
          >
            <h5 style={{ margin: '0px' }}>{getPassValidityText()}</h5>
          </MjmlText>
        )}
        <MjmlText
          align="center"
          paddingTop="0px"
          paddingBottom="0px"
          color="#52565B"
        >
          <>
            {visitorPass.startDate}
            {visitorPass.endDate && ` to ${visitorPass.endDate}`}
            <br />
            {visitorPass.startTime} {i18n.t('punctuation.dash')}{' '}
            {visitorPass.endTime} {building.timezone}
          </>
        </MjmlText>
        <MjmlText
          align="center"
          paddingTop="24px"
          paddingBottom="0px"
          color="#1D1E20"
        >
          <h5 style={{ margin: '0px' }}>
            {i18n.t('shared.email.visitorManagement.visitorPass.host')}
          </h5>
        </MjmlText>
        <MjmlText
          align="center"
          paddingTop="0px"
          paddingBottom="0px"
          color="#52565B"
        >
          <>
            {host.firstName} {host.lastName}
            <br />
            {host.companyName}
          </>
        </MjmlText>
        {host.floor && (
          <>
            <MjmlText
              align="center"
              paddingTop="24px"
              paddingBottom="0px"
              color="#1D1E20"
            >
              <h5 style={{ margin: '0px' }}>
                {i18n.t(
                  'shared.email.visitorManagement.visitorPass.floor.title'
                )}
              </h5>
            </MjmlText>
            <MjmlText
              align="center"
              paddingTop="0px"
              paddingBottom="0px"
              color="#52565B"
              fontSize="14px"
            >
              <>
                {i18n.t('shared.email.visitorManagement.visitorPass.floor', {
                  floor: host.floor,
                })}
              </>
            </MjmlText>
          </>
        )}
        {visitorPass.notesToVisitor && (
          <>
            <MjmlText
              align="center"
              paddingTop="24px"
              paddingBottom="0px"
              color="#1D1E20"
            >
              <h5 style={{ margin: '0px' }}>
                {i18n.t('shared.email.visitorManagement.visitorPass.notes')}
              </h5>
            </MjmlText>
            <MjmlText
              align="center"
              paddingTop="0px"
              paddingBottom="0px"
              color="#52565B"
            >
              <>{visitorPass.notesToVisitor}</>
            </MjmlText>
          </>
        )}
        {appleWallet && isCredentialShown && (
          <MjmlImage
            width="150px"
            href={appleWallet.link}
            src="https://support.apple.com/library/content/dam/edam/applecare/images/en_US/iOS/add-to-apple-wallet-logo.png"
          />
        )}
        {calendarLinks && (
          <MjmlText
            align="center"
            paddingBottom="10%"
            fontSize="14px"
            color="#52565B"
          >
            {i18n.t('shared.email.visitorManagement.visitorPass.addToCalendar')}{' '}
            <br />
            {Object.entries(calendarLinks).map(([name, link]) => (
              <a key={name} href={link}>
                {name}
              </a>
            ))}
          </MjmlText>
        )}
      </MjmlColumn>
    </MjmlSection>
  );
}
